import { cn } from "./classNames-DcGnTh2N.js";
import { createChangeInputEvent } from "./utils-p6tjltbX.js";
import { mergeRefs } from "./internal/utils/mergeRefs.js";
import { Pipette, X } from "./icons-ZIeZXXS7.js";
import { InputGroup } from "./InputGroup-Dm014ruw.js";
import { useControllableState } from "./useControllableState-CUYXMsfz.js";
import { useRef } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
//#region src/blocks/ColorInput/utils.ts
const HEX_CHARSET = /^[0-9a-fA-F]+$/;
const COMPLETE_HEX_PATTERN = /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
const MAX_HEX_BODY_LENGTH = 8;
const PICKER_FALLBACK = "#000000";
const extractHexBody = (value) => {
	if (typeof value !== "string") return null;
	const trimmed = value.trim();
	if (trimmed === "") return null;
	return trimmed.startsWith("#") ? trimmed.slice(1) : trimmed;
};
const isCompleteHexColor = (value) => COMPLETE_HEX_PATTERN.test(value);
const showPicker = (input) => {
	"showPicker" in HTMLInputElement.prototype ? input.showPicker() : input.click();
};
/**
* Coerces free-form text into a partial hex color (up to `#rrggbbaa`) for the text input.
*
* - Adds a missing `#`, keeps only the leading hex digits.
* - `''` for non-strings or when no hex digits remain.
* - May be a prefix (`#ab`) — gate rendering on `isCompleteHexColor`.
*/
const coerceHexInputValue = (value) => {
	const body = extractHexBody(value);
	if (body === null) return "";
	const [validBody] = body.match(/^[0-9a-fA-F]+/) ?? [];
	if (!validBody) return "";
	return `#${validBody.slice(0, MAX_HEX_BODY_LENGTH)}`;
};
/**
* Coerces any value into the lowercase `#rrggbb` the native `<input type="color">` requires.
*
* - Shorthand is expanded, alpha dropped, anything invalid → `#000000`.
* - Lowercased because the native picker lowercases, so values round-trip.
*/
const coerceColorPickerValue = (value) => {
	const body = extractHexBody(value);
	if (body === null || !HEX_CHARSET.test(body)) return PICKER_FALLBACK;
	if (body.length === 3 || body.length === 4) {
		const [r, g, b] = body;
		return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
	}
	if (body.length === 6 || body.length === 8) return `#${body.slice(0, 6).toLowerCase()}`;
	return PICKER_FALLBACK;
};
//#endregion
//#region src/blocks/ColorInput/ColorPicker.tsx
const ColorPicker = ({ value, disabled, readOnly, ...props }) => {
	const pickerRef = useRef(null);
	const handleOpenPicker = () => {
		if (!pickerRef.current) return;
		showPicker(pickerRef.current);
	};
	return /* @__PURE__ */ jsxs("div", {
		className: "tw:relative",
		children: [/* @__PURE__ */ jsx("input", {
			...props,
			ref: pickerRef,
			type: "color",
			value: coerceColorPickerValue(value),
			tabIndex: -1,
			"aria-hidden": true,
			className: "tw:pointer-events-none tw:absolute tw:left-0 tw:top-full tw:size-0 tw:opacity-0",
			disabled,
			readOnly
		}), /* @__PURE__ */ jsx(InputGroup.Button, {
			onMouseDown: (event) => event.preventDefault(),
			disabled: disabled || readOnly,
			onClick: handleOpenPicker,
			"aria-label": "Pick color",
			children: /* @__PURE__ */ jsx(Pipette, { className: "tw:size-4" })
		})]
	});
};
//#endregion
//#region src/blocks/ColorInput/ColorInput.tsx
const ColorInput = ({ ref, state, variant, focusVariant, sizing, className, value: valueProp, onValueChange, onValueBlur, onChange, onBlur, defaultValue, allowClear = false, disabled, readOnly, ...restInputProps }) => {
	const inputRef = useRef(null);
	const [value, setValue] = useControllableState({
		prop: valueProp === null ? "" : valueProp,
		defaultProp: defaultValue ?? "",
		onChange: onValueChange
	});
	const isControlled = valueProp !== void 0;
	const canClear = allowClear && !!value && !disabled && !readOnly;
	const swatchColor = coerceHexInputValue(value);
	const isValidSwatch = isCompleteHexColor(swatchColor);
	const handleChange = (event) => {
		setValue(event.target.value);
		onChange?.(event);
	};
	const handleBlur = (event) => {
		const newValue = event.target.value;
		const sanitizedNewValue = coerceHexInputValue(newValue);
		if (sanitizedNewValue !== newValue) {
			if (!isControlled) event.target.value = sanitizedNewValue;
			setValue(sanitizedNewValue);
		}
		onValueBlur?.(sanitizedNewValue);
		onBlur?.(event);
	};
	const handlePickerChange = (event) => {
		setValue(coerceHexInputValue(event.target.value));
		onChange?.(event);
	};
	const handleClear = () => {
		if (!inputRef.current) return;
		inputRef.current.value = "";
		setValue("");
		onChange?.(createChangeInputEvent(inputRef.current));
	};
	return /* @__PURE__ */ jsxs(InputGroup, {
		state,
		variant,
		sizing,
		focusVariant,
		className,
		children: [
			/* @__PURE__ */ jsx(InputGroup.Addon, {
				align: "inline-start",
				className: cn({ "tw:-ml-7 tw:opacity-0": !value }, "tw:transition-[opacity,margin]", "tw:ease-linear", "tw:duration-200"),
				children: /* @__PURE__ */ jsx("div", {
					style: isValidSwatch ? { background: swatchColor } : void 0,
					className: cn("tw:size-5", "tw:shrink-0", "tw:rounded-sm", "tw:border", "tw:border-solid", "tw:border-neutral-300", { "tw:bg-[linear-gradient(to_top_right,transparent_calc(50%_-_1px),theme(colors.red.300)_calc(50%_-_1px),theme(colors.red.300)_calc(50%_+_1px),transparent_calc(50%_+_1px))]": !isValidSwatch })
				})
			}),
			/* @__PURE__ */ jsx(InputGroup.Input, {
				...restInputProps,
				type: "text",
				ref: mergeRefs(ref ?? null, inputRef),
				value,
				onChange: handleChange,
				onBlur: handleBlur,
				disabled,
				readOnly
			}),
			/* @__PURE__ */ jsxs(InputGroup.Addon, {
				align: "inline-end",
				children: [canClear && /* @__PURE__ */ jsx(InputGroup.Button, {
					onMouseDown: (event) => event.preventDefault(),
					onClick: handleClear,
					"aria-label": "Clear color",
					children: /* @__PURE__ */ jsx(X, { className: "tw:size-4" })
				}), /* @__PURE__ */ jsx(ColorPicker, {
					disabled,
					readOnly,
					value,
					onChange: handlePickerChange
				})]
			})
		]
	});
};
//#endregion
export { ColorInput };

//# sourceMappingURL=ColorInput-D1nC0X6i.js.map